import { useState } from 'react';
import { api, ApiError, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { CommunityProject, CommunityProjectStatus, Page, ProjectCategory } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatusBadge } from '../../ui/components';
import { ProjectDetail } from './ProjectDetail';

const STATUS_OPTIONS: CommunityProjectStatus[] = [
  'PROPOSED',
  'UNDER_REVIEW',
  'SHORTLISTED',
  'SELECTED',
  'NOT_SELECTED',
  'IN_DELIVERY',
  'COMPLETED',
  'REJECTED',
  'CANCELLED',
];

const CATEGORY_OPTIONS: ProjectCategory[] = [
  'ROAD_DRAINAGE',
  'WATER_SANITATION',
  'COMMUNITY_HALL',
  'SPORTS_RECREATION',
  'ENVIRONMENT_BEAUTIFICATION',
  'YOUTH_ELDERLY',
  'MARKET_ECONOMIC',
  'OTHER',
];

/**
 * Community-project proposal review / delivery / milestones. GET /projects
 * is a public endpoint but always queried with ?district=<own district>
 * here; the review/shortlist/deliver/complete/cancel/milestone actions live
 * in ProjectDetail.tsx and are all staff- or admin-gated by M41 itself.
 */
export function ProjectsConsole() {
  const { persona } = useAuth();
  const district = persona?.district ?? '';
  const [status, setStatus] = useState<CommunityProjectStatus | ''>('');
  const [category, setCategory] = useState<ProjectCategory | ''>('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showPropose, setShowPropose] = useState(false);

  const qs = new URLSearchParams({ limit: '100' });
  if (district) qs.set('district', district);
  if (status) qs.set('status', status);
  if (category) qs.set('category', category);
  const projectsQ = useApiQuery<Page<CommunityProject>>(district ? `/api/v1/m41/projects?${qs.toString()}` : null);
  const projects = projectsQ.data?.items ?? [];

  if (!district) return <ErrorNote message="This account has no district binding on file." />;

  return (
    <div>
      <PageHeader
        title="Community Projects"
        subtitle="Review participatory-budgeting proposals through to delivery and milestones."
        actions={
          <button className="btn-secondary" onClick={() => setShowPropose(true)}>
            + Log a proposal
          </button>
        }
      />

      <div className="mb-4 flex flex-wrap gap-3">
        <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value as CommunityProjectStatus | '')}>
          <option value="">All statuses</option>
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {s.replaceAll('_', ' ')}
            </option>
          ))}
        </select>
        <select className="field-input !w-auto" value={category} onChange={(e) => setCategory(e.target.value as ProjectCategory | '')}>
          <option value="">All categories</option>
          {CATEGORY_OPTIONS.map((c) => (
            <option key={c} value={c}>
              {c.replaceAll('_', ' ')}
            </option>
          ))}
        </select>
      </div>

      {projectsQ.error ? (
        <div className="mb-4">
          <ErrorNote message={projectsQ.error} />
        </div>
      ) : null}

      <div className="card overflow-x-auto">
        {projectsQ.loading ? (
          <Spinner />
        ) : projects.length === 0 ? (
          <EmptyState title="No projects match these filters" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Ref</th>
                <th className="table-head">Title</th>
                <th className="table-head">Category</th>
                <th className="table-head text-right">Est. cost</th>
                <th className="table-head text-right">Votes</th>
                <th className="table-head">Status</th>
              </tr>
            </thead>
            <tbody>
              {projects.map((p) => (
                <tr key={p.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedId(p.id)}>
                  <td className="table-cell font-mono text-xs">{p.refNo}</td>
                  <td className="table-cell font-medium">{p.title}</td>
                  <td className="table-cell text-xs text-slate-500">{p.category.replaceAll('_', ' ')}</td>
                  <td className="table-cell text-right tabular-nums">{scr(p.estimatedCostScr)}</td>
                  <td className="table-cell text-right tabular-nums">{p.voteCount}</td>
                  <td className="table-cell">
                    <StatusBadge status={p.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selectedId ? (
        <ProjectDetail
          id={selectedId}
          onClose={() => setSelectedId(null)}
          onChanged={() => {
            projectsQ.reload();
          }}
        />
      ) : null}
      {showPropose ? (
        <ProposeProjectModal
          district={district}
          onClose={() => setShowPropose(false)}
          onDone={() => {
            setShowPropose(false);
            projectsQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function ProposeProjectModal({ district, onClose, onDone }: { district: string; onClose: () => void; onDone: () => void }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<ProjectCategory>('OTHER');
  const [estimatedCostScr, setEstimatedCostScr] = useState('');
  const [proposerContact, setProposerContact] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m41/projects', {
        district,
        title: title.trim(),
        description: description.trim(),
        category,
        estimatedCostScr: Number.parseFloat(estimatedCostScr || '0'),
        proposerContact: proposerContact.trim() || undefined,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to log proposal');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Log a community-project proposal" onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
          M41's proposal endpoint attributes proposals to whoever calls it — there is no "on behalf of a resident"
          field, so this logs the proposal under your own staff identity, for a resident who came to the office
          in person.
        </p>
        <Field label="Title">
          <input className="field-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Beau Vallon Beachfront Lighting Upgrade" />
        </Field>
        <Field label="Description">
          <textarea className="field-input" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Category">
            <select className="field-input" value={category} onChange={(e) => setCategory(e.target.value as ProjectCategory)}>
              {CATEGORY_OPTIONS.map((c) => (
                <option key={c} value={c}>
                  {c.replaceAll('_', ' ')}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Estimated cost (SCR)">
            <input className="field-input" type="number" min="1" step="0.01" value={estimatedCostScr} onChange={(e) => setEstimatedCostScr(e.target.value)} />
          </Field>
        </div>
        <Field label="Proposer contact (optional)">
          <input className="field-input" value={proposerContact} onChange={(e) => setProposerContact(e.target.value)} placeholder="phone or email of the resident" />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-primary"
            onClick={submit}
            disabled={busy || title.trim().length < 2 || description.trim().length < 2 || Number.parseFloat(estimatedCostScr || '0') <= 0}
          >
            {busy ? 'Logging…' : 'Log proposal'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
